import "./EditableDropDown.scss";

import * as R from "ramda";

import { Button, ButtonFrameworkVariant, ButtonType, Dropdown } from ".";
import { MdAdd, MdContentCopy, MdDelete, MdEdit, MdMoreVert } from "react-icons/md";
import React, { useMemo, useRef, useState } from "react";

import { OverlayTrigger } from "./OverlayTrigger";
import { ReactComponent as RenameIcon } from "../SingleChannel/RenameIcon.svg";
import { Tooltip } from "react-bootstrap";
import cn from "classnames";
import { transformEmailToFullName } from "../SingleChannel/MetricsTable/metricsTableUtils";
import { useSetError } from "../redux/modals";

export interface MenuItem {
  id: any;
  label: any;
  updatedBy?: string;
  updatedAt?: string;
  isDeletable?: boolean;
}

// presence of button functions (or lack thereof) determing buttons existence
export interface EditableDropDownProps {
  design?: "primary" | "secondary";
  addItemOnClick?: () => void;
  addItemText?: string;
  allowNoSelections?: boolean;
  applyOnClick?: () => void;
  cancelOnClick?: () => void;
  className?: string;
  defaultItems?: string[];
  deleteItemOnClick?: (item: MenuItem) => void;
  duplicateItemOnClick?: (item: MenuItem) => void;
  renameItemOnClick?: (item: MenuItem) => void;
  editItemOnClick?: (item: MenuItem) => void;
  actionMenuSubtitles?: string[];
  hasTicks?: boolean;
  dropdownLabel?: string;
  menuItems: MenuItem[];
  selectedItem: MenuItem | undefined;
  setSelectedItem: (item: MenuItem | undefined) => void;
  leadingIcon?: JSX.Element;
  onChange?: (string) => void;
  disabledDeleteTooltipText?: string;
}

export const EditableDropDown: React.FC<EditableDropDownProps> = React.memo(
  ({
    design = "primary",
    addItemOnClick,
    addItemText = "",
    allowNoSelections = true,
    applyOnClick,
    cancelOnClick,
    className = "",
    defaultItems = [],
    deleteItemOnClick,
    duplicateItemOnClick,
    renameItemOnClick,
    editItemOnClick,
    actionMenuSubtitles = [],
    hasTicks = false,
    dropdownLabel = "",
    menuItems,
    selectedItem,
    setSelectedItem,
    leadingIcon,
    onChange = () => {},
    disabledDeleteTooltipText,
  }) => {
    const setError = useSetError();
    const [selectedOptionTrigger, setSelectedOptionTrigger] = useState<number | undefined>(
      undefined
    );
    const [showDropdown, setShowDropdown] = useState<boolean>(false);
    const disabledActionClickedRef = useRef(false);

    const processedMenuItems = useMemo(
      () =>
        R.map(item => {
          const isDefault =
            !R.defaultTo(item.label) && !defaultItems.includes(item.label.toLowerCase());
          const last_user =
            !R.isNil(item) && !R.isNil(item.updatedBy)
              ? transformEmailToFullName(item.updatedBy)
              : isDefault
              ? "Default Preset"
              : "N/A";
          const isDeletable = item.isDeletable !== undefined ? item.isDeletable : true;
          /// render tooltip when !isDeletable & disabledDeleteTooltipText is provided
          const renderDeletePresetOption = (deleteItemOnClick, isDeletable) => {
            const deletePresetOption = (
              <div
                className={cn("presetOption", { disabledDelete: !isDeletable })}
                onClick={
                  isDeletable
                    ? () => {
                        deleteItemOnClick(item);
                        setSelectedOptionTrigger(undefined);
                      }
                    : () => {
                        disabledActionClickedRef.current = true;
                      }
                }
              >
                <div className="presetOptionIcon">
                  <MdDelete />
                </div>
                <div className="presetOptionText">Delete</div>
              </div>
            );
            return !isDeletable && disabledDeleteTooltipText ? (
              <OverlayTrigger
                overlay={<Tooltip id="delete-tooltip">{disabledDeleteTooltipText}</Tooltip>}
                yoffset={5}
              >
                {deletePresetOption}
              </OverlayTrigger>
            ) : (
              deletePresetOption
            );
          };
          return {
            className: `${selectedOptionTrigger === item.id ? "dropdownOpen" : ""}`,
            label: item.label,
            value: `${item.id}` as string,
            isDeletable: isDeletable,
            itemOptions: (
              <OverlayTrigger
                customTrigger={selectedOptionTrigger === item.id}
                placement="right top"
                xoffset={0}
                yoffset={9}
                overlay={
                  <div className="presetOptionsPresetDropdown">
                    {editItemOnClick !== undefined ? (
                      <div className="presetOption" onClick={() => editItemOnClick(item.id)}>
                        <div className="presetOptionIcon">{<MdEdit />}</div>
                        <div className="presetOptionText">{"Edit"}</div>
                      </div>
                    ) : (
                      <></>
                    )}
                    {renameItemOnClick !== undefined ? (
                      <div
                        className="presetOption"
                        onClick={() => {
                          if (isDefault) {
                            setError({ message: "Cannot rename default presets!" });
                          } else {
                            renameItemOnClick(item);
                          }
                        }}
                      >
                        <div className="presetOptionIcon">
                          <RenameIcon />
                        </div>
                        <div className="presetOptionText">Rename</div>
                      </div>
                    ) : (
                      <></>
                    )}
                    {deleteItemOnClick !== undefined &&
                      renderDeletePresetOption(deleteItemOnClick, isDeletable)}
                    {duplicateItemOnClick !== undefined ? (
                      <div className="presetOption" onClick={() => duplicateItemOnClick(item.id)}>
                        <div className="presetOptionIcon">
                          <MdContentCopy />
                        </div>
                        <div className="presetOptionText">Duplicate</div>
                      </div>
                    ) : (
                      <></>
                    )}
                    <div className="presetLastModifiedContainer">
                      <div className="presetLastModifiedText">Created By: {last_user}</div>
                    </div>
                  </div>
                }
              >
                <div
                  className="presetOptionExpander"
                  onClick={() =>
                    setSelectedOptionTrigger(
                      selectedOptionTrigger === item.id ? undefined : item.id
                    )
                  }
                >
                  <MdMoreVert />
                </div>
              </OverlayTrigger>
            ),
            selected: selectedItem && selectedItem.id === item.id,
          };
        }, menuItems),
      [
        defaultItems,
        deleteItemOnClick,
        duplicateItemOnClick,
        editItemOnClick,
        renameItemOnClick,
        selectedOptionTrigger,
        selectedItem,
        setError,
        menuItems,
        disabledDeleteTooltipText,
      ]
    );

    const addItemButton = (
      <Button
        className="addNewPresetButton"
        icon={<MdAdd />}
        type={ButtonType.EMPTY}
        variant={ButtonFrameworkVariant.LEADING_ICON}
      >
        {addItemText}
      </Button>
    );

    return (
      <div className={`presetDropdown ${className} ${selectedItem ? "presetSelected" : ""}`}>
        {menuItems && (
          <Dropdown
            design={design}
            applyOnClick={applyOnClick}
            hideApplyOnClick={applyOnClick === undefined}
            cancelOnClick={cancelOnClick}
            hideCancelOnClick={cancelOnClick === undefined}
            footer={addItemOnClick === undefined ? <></> : addItemButton}
            footerOnClick={addItemOnClick}
            hasFilterBar={false}
            hasTicks={hasTicks}
            label={
              <div className={`${!selectedItem && !showDropdown ? "placeholderLabel" : ""}`}>
                {!R.isNil(selectedItem) ? selectedItem.label : dropdownLabel}
              </div>
            }
            leadingIcon={leadingIcon}
            menuItems={processedMenuItems}
            multiSelect={[false, false]}
            onChange={value => {
              setSelectedItem(menuItems.find(item => item.id === Number.parseInt(value)));
              setShowDropdown(false);
            }}
            show={showDropdown}
            setShow={setShowDropdown}
            subTitles={actionMenuSubtitles}
            onToggle={isOpen => {
              if (!R.isNil(selectedOptionTrigger)) {
                if (disabledActionClickedRef.current) {
                  disabledActionClickedRef.current = false;
                } else {
                  setShowDropdown(true);
                  setSelectedOptionTrigger(undefined);
                }
              } else {
                setShowDropdown(isOpen);
              }
            }}
            options={[]}
            value={""}
          />
        )}
      </div>
    );
  }
);

export default EditableDropDown;
